import styled from '@emotion/styled';
import {
  EntityEventState,
  EntityEventType,
  type EntityEventTypeBasicAlert,
} from '@racemap/sdk/schema/entityEvents';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import { Button, Flex, Spin } from 'antd';
import { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { AutoSizer, List } from 'react-virtualized';
import { useCurrentUser, useEvent } from 'src/lib/customHooks';
import { useEventGeoElements } from 'src/lib/customHooks/useEventGeoElements';
import { useAlerts } from 'src/store/monitor_beta/monitorStore';
import { IconFilter } from '../Icon';
import { AlertEntry } from './AlertEntry';
import { AlertsFilter, type AssignedToType } from './AlertsFilter';
import { filterAlertsAroundProtectedZones } from './utils/filterAlertsAroundProtectedZones';
import { getAlertsByAssignment } from './utils/getAlertsByAssignment';
import { getFilteredAlerts } from './utils/getFilteredAlerts';

type Params = {
  eventSlug: string;
};

export const basicAlertEventTypes: Array<EntityEventTypeBasicAlert> = [
  EntityEventType.LOW_BATTERY,
  EntityEventType.CRASH,
  EntityEventType.NO_MOVEMENT,
  EntityEventType.SOS,
  EntityEventType.POWER_OFF,
];

export const Alerts: FC = () => {
  const { eventSlug } = useParams<Params>();
  const { event, isLoading } = useEvent({ slug: eventSlug });
  const { geoElements } = useEventGeoElements({ eventId: event?.id });
  const currentUser = useCurrentUser();

  const [startTime, endTime] = currentEventTimes(event);
  const startAlertFrame =
    startTime !== undefined ? DateTime.fromMillis(startTime).minus({ hours: 8 }) : DateTime.now();
  const endAlertFrame =
    endTime !== undefined ? DateTime.fromMillis(endTime).plus({ hours: 8 }) : DateTime.now();

  const [alertEventTypes, setAlertEventTypes] = useState(basicAlertEventTypes);

  const [dateRange, setDateRange] = useState<[DateTime, DateTime]>([
    startAlertFrame,
    endAlertFrame,
  ]);

  useEffect(() => {
    const [newStartTime, newEndTime] = currentEventTimes(event);
    const newStartAlertFrame =
      newStartTime !== undefined
        ? DateTime.fromMillis(newStartTime).minus({ hours: 8 })
        : DateTime.now();
    const newEndAlertFrame =
      newEndTime !== undefined
        ? DateTime.fromMillis(newEndTime).plus({ hours: 8 })
        : DateTime.now();
    setDateRange([newStartAlertFrame, newEndAlertFrame]);
  }, [event]);

  const [assignedTo, setAssignedTo] = useState<AssignedToType>('all');

  const [stateFilter, setStateFilter] = useState([
    EntityEventState.NEW,
    EntityEventState.IN_PROGRESS,
    EntityEventState.STANDBY,
  ]);

  const [starterDeviceIds, setStarterDeviceIds] = useState<Array<string>>([]);
  const [starterIds, setStarterIds] = useState<Array<string>>([]);

  const effectiveAlertEventTypes = useMemo(() => {
    return alertEventTypes.length > 0 ? alertEventTypes : basicAlertEventTypes;
  }, [alertEventTypes]);

  const alerts = useAlerts();

  const alertsOutsideOfProtectedZones = useMemo(() => {
    if (!geoElements || event == null) return [];
    return filterAlertsAroundProtectedZones({
      event,
      features: geoElements.features,
      alerts,
    });
  }, [alerts, geoElements, event]);

  const filteredAlerts = useMemo(() => {
    const filtered = getFilteredAlerts(
      alertsOutsideOfProtectedZones,
      effectiveAlertEventTypes,
      dateRange,
      starterDeviceIds,
      stateFilter,
      starterIds,
    );
    return getAlertsByAssignment(filtered, assignedTo, currentUser).sort(
      (a, b) => b.occurredAt.getTime() - a.occurredAt.getTime(),
    );
  }, [
    alertsOutsideOfProtectedZones,
    assignedTo,
    currentUser,
    alertEventTypes,
    dateRange,
    starterDeviceIds,
    stateFilter,
    starterIds,
  ]);

  const [filterVisible, setFilterVisible] = useState(false);

  if (isLoading || !event)
    return (
      <Container>
        <Spin />
      </Container>
    );

  return (
    <Flex vertical style={{ height: '100%' }}>
      <SortFilterHeader>
        <FilterButton icon={<IconFilter />} onClick={() => setFilterVisible((prev) => !prev)} />
      </SortFilterHeader>
      <FilterContainer isVisible={filterVisible}>
        <AlertsFilter
          onAlertEventTypeChange={setAlertEventTypes}
          onDateRangeChange={setDateRange}
          onAssignedToChange={setAssignedTo}
          onStateFilterChange={setStateFilter}
          onStarterDeviceIdsChange={setStarterDeviceIds}
          onStarterIdsChange={setStarterIds}
          dateRange={dateRange}
          assignedTo={assignedTo}
          stateFilter={stateFilter}
          minDate={startAlertFrame}
          maxDate={endAlertFrame}
          eventId={eventSlug}
        />
      </FilterContainer>
      {filteredAlerts.length === 0 ? (
        <EmptyState>No alerts found.</EmptyState>
      ) : (
        <div style={{ flex: 1 }}>
          <AutoSizer>
            {({ height, width }) => (
              <div style={{ width, height }} id="alerts-list">
                <List
                  width={width}
                  height={height}
                  rowCount={filteredAlerts.length}
                  rowHeight={105}
                  rowRenderer={({ index, key, style }) => {
                    const alert = filteredAlerts[index];
                    return (
                      <div key={key} style={style}>
                        <AlertEntry alert={alert} currentUser={currentUser} event={event} />
                      </div>
                    );
                  }}
                />
              </div>
            )}
          </AutoSizer>
        </div>
      )}
    </Flex>
  );
};

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: ${RacemapColors.DarkGray};
`;

const SortFilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: ${RacemapColors.LightGray};
`;

const FilterButton = styled(Button)`
  margin-left: auto;
`;

const FilterContainer = styled.div<{ isVisible: boolean }>`
  background: ${RacemapColors.LightLightGray};
  padding: ${({ isVisible }) => (isVisible ? '24px 12px 12px 12px' : '0')};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  transition: opacity 0.3s ease, max-height 0.3s ease, padding 0.3s ease;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  max-height: ${({ isVisible }) => (isVisible ? '600px' : '0')};
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100px;
`;
